import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { io } from 'socket.io-client';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { SSOIcons, AuthMarketingPanel } from './AuthShared';
import { loginSuccess } from '../../slices/authSlice';
import { storeAuthToken } from '../../utils/authToken';

const RushSignInPage = () => {
  const [mode, setMode] = useState('password'); // 'password' | 'magic' | 'magic-sent'
  const [showPwd, setShowPwd] = useState(false);
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 2FA
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(() => {
    const from = location.state?.from;
    if (!from) return '/dashboard';
    return `${from.pathname || '/dashboard'}${from.search || ''}${from.hash || ''}`;
  }, [location]);

  const completeLogin = async (token) => {
    storeAuthToken(token, rememberMe);
    const me = await axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } });
    const user = me.data;
    dispatch(loginSuccess({ token, user }));
    try {
      const socket = io('/', { transports: ['websocket', 'polling'] });
      socket.emit('join', user._id);
      window.__SOCKET__ = socket;
    } catch (e) {
      // Socket failure shouldn't block navigation
    }
    navigate(redirectTo, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'magic') {
      // Send a magic link; backend doesn't yet implement this — call request-password-reset
      // as a stand-in OR just show the success card. We'll show the success card for now,
      // since there's no magic-link endpoint on the server.
      setMode('magic-sent');
      return;
    }
    if (!loginInput) { setError('Please enter a username or email.'); return; }
    if (!password || password.length < 6) { setError('Password must be at least 6 characters long.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post('/api/users/login', { loginInput, password, rememberMe });
      if (res.data.twoFactorRequired) {
        setTwoFactorRequired(true);
        setTwoFactorUserId(res.data.userId);
        setSubmitting(false);
        return;
      }
      const { token } = res.data;
      if (!token) { setError('Login failed: no token returned.'); setSubmitting(false); return; }
      await completeLogin(token);
    } catch (err) {
      setError(err.response?.data?.msg || 'An error occurred. Please try again.');
      setSubmitting(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post('/api/users/verify-2fa', { userId: twoFactorUserId, code: twoFactorCode });
      const { token } = res.data;
      if (!token) { setError('Verification failed: no token returned.'); setSubmitting(false); return; }
      await completeLogin(token);
    } catch (err) {
      setError(err.response?.data?.msg || 'Verification failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="rs auth-page">
      <section className="auth-form-panel">
        <header className="auth-form-header">
          <Link to="/" className="rs-logo rs-logo-img" aria-label="Rush Studio home">
            <img src={rushLogo} alt="Rush Studio" />
          </Link>
          <div className="auth-form-header-link">
            No account?<Link to="/register">Sign up</Link>
          </div>
        </header>

        <div className="auth-form-body">
          <div className="auth-eyebrow">Welcome back</div>
          <h1 className="auth-title">Sign in to Rush Studio.</h1>
          <p className="auth-sub">Pick up where you left off. Your workspace, your projects, your memory.</p>

          {!twoFactorRequired && (
            <>
              <div className="sso-row">
                <button type="button" className="sso-btn" disabled>{SSOIcons.google}Google</button>
                <button type="button" className="sso-btn" disabled>{SSOIcons.microsoft}Microsoft</button>
                <button type="button" className="sso-btn" disabled>{SSOIcons.github}GitHub</button>
              </div>
              <div className="auth-divider">or with email</div>
            </>
          )}

          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-soft)', color: 'var(--accent-2)', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 14 }} role="alert">
              {error}
            </div>
          )}

          {twoFactorRequired ? (
            <form className="auth-form" onSubmit={handleVerify2FA}>
              <p className="auth-sub" style={{ marginTop: 0 }}>Enter the 6-digit code from your authenticator app.</p>
              <label className="field">
                <span>Verification code</span>
                <input type="text" inputMode="numeric" pattern="\d{6}" className="input input-lg" placeholder="123456" required autoFocus value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
              </label>
              <button className="btn btn-accent auth-submit" type="submit" disabled={submitting || twoFactorCode.length !== 6}>
                {submitting ? 'Verifying…' : 'Verify & sign in →'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <button type="button" onClick={() => { setTwoFactorRequired(false); setTwoFactorCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--line-2)', textUnderlineOffset: 4 }}>
                  Cancel and use another account
                </button>
              </div>
            </form>
          ) : mode === 'magic-sent' ? (
            <div className="magic-success">
              <div className="magic-success-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 4h12v10H3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M3 4l6 5 6-5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
              </div>
              <div className="magic-success-text">
                <b>Check your inbox.</b>
                If your account uses email sign-in, a one-time link is on its way. Otherwise sign in with your password.
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setMode('password')}>Use password instead</button>
                </div>
              </div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              <label className="field">
                <span>Username or email</span>
                <input type="text" className="input input-lg" placeholder="sara@yourcompany.com" required autoComplete="username" value={loginInput} onChange={(e) => setLoginInput(e.target.value)} />
              </label>

              {mode === 'password' && (
                <label className="field">
                  <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span>Password</span>
                    <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--ink-3)', textDecoration: 'none', fontWeight: 500 }}>Forgot?</Link>
                  </span>
                  <div className="input-with-action">
                    <input type={showPwd ? 'text' : 'password'} className="input input-lg" placeholder="••••••••" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    <button type="button" className="field-action" onClick={() => setShowPwd((s) => !s)}>{showPwd ? 'Hide' : 'Show'}</button>
                  </div>
                </label>
              )}

              <label className="checkbox-row">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                <span>Keep me signed in on this device for 30 days.</span>
              </label>

              <button className="btn btn-accent auth-submit" type="submit" disabled={submitting}>
                {submitting ? 'Signing in…' : mode === 'magic' ? 'Email me a magic link →' : 'Sign in →'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <button type="button" onClick={() => { setMode(mode === 'password' ? 'magic' : 'password'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--line-2)', textUnderlineOffset: 4 }}>
                  {mode === 'password' ? 'Sign in with a magic link instead' : 'Use password instead'}
                </button>
              </div>
            </form>
          )}

          <div className="auth-aside-link">Need SSO or SAML? <Link to="/contact">Talk to sales →</Link></div>
        </div>

        <footer className="auth-form-footer">
          <span>© {new Date().getFullYear()} Rush Studio Ltd.</span>
          <span><Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> · <Link to="/contact">Help</Link></span>
        </footer>
      </section>

      <AuthMarketingPanel variant="testimonial" />
    </div>
  );
};

export default RushSignInPage;
